import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { FunnelDefinition, FunnelTemplate, RawDataTemplate } from '../models/funnel.types'

import type { ApiError } from '@/shared/http'

export type FunnelWorkspaceState = 'idle' | 'loading' | 'ready' | 'error'

export const useFunnelStore = defineStore('audienceFunnel', () => {
  const workspaceKey = ref<string | null>(null)
  const definitions = ref<FunnelDefinition[]>([])
  const templates = ref<FunnelTemplate[]>([])
  const rawTypes = ref<RawDataTemplate[]>([])
  const ga4EventNames = ref<string[]>([])
  const optionsLoaded = ref(false)
  const state = ref<FunnelWorkspaceState>('idle')
  const error = ref<ApiError | null>(null)
  const activeTabName = ref<string | null>(null)

  function setLoading(workspaceId: number | string): void {
    const nextKey = String(workspaceId)
    if (workspaceKey.value !== nextKey) {
      resetState()
      workspaceKey.value = nextKey
    }
    state.value = 'loading'
    error.value = null
  }

  function setDefinitions(next: FunnelDefinition[]): void {
    definitions.value = next
  }

  function setOptions(input: {
    templates: FunnelTemplate[]
    rawTypes: RawDataTemplate[]
    ga4EventNames: string[]
  }): void {
    templates.value = input.templates
    rawTypes.value = input.rawTypes
    ga4EventNames.value = input.ga4EventNames
    optionsLoaded.value = true
  }

  function setReady(): void {
    state.value = 'ready'
    error.value = null
  }

  function setError(next: ApiError): void {
    state.value = 'error'
    error.value = next
  }

  function setActiveTab(name: string | null): void {
    activeTabName.value = name
  }

  function resetState(): void {
    workspaceKey.value = null
    definitions.value = []
    templates.value = []
    rawTypes.value = []
    ga4EventNames.value = []
    optionsLoaded.value = false
    state.value = 'idle'
    error.value = null
    activeTabName.value = null
  }

  return {
    workspaceKey,
    definitions,
    templates,
    rawTypes,
    ga4EventNames,
    optionsLoaded,
    state,
    error,
    activeTabName,
    setLoading,
    setDefinitions,
    setOptions,
    setReady,
    setError,
    setActiveTab,
    resetState,
  }
})

export type FunnelStore = ReturnType<typeof useFunnelStore>
